import _ from 'lodash';

import {
  CHECKLIST_READ_DEPTH_BY_ENTITY_TYPE,
  CHECKLIST_RESULT_STATE_STATUS_BY_DISPLAY_TYPE,
  ChecklistDisplaySettingType,
  ChecklistEntityType,
  ChecklistItemDeterminationType,
  ChecklistItemValueFormat,
  ChecklistReadDepth,
  ChecklistResultState,
} from 'constants/checklists';
import { COMMON_DATE_FORMAT } from 'constants/date';
import { StatusEnum } from 'constants/status';

import { formatDate } from './date';
import { formatNumberToCurrencyString } from './number';

const formatChecklistValueByType = {
  [ChecklistItemValueFormat.CURRENCY]: value => {
    const numericValue = Number(value);

    if (_.isNaN(numericValue)) {
      return value;
    }

    return formatNumberToCurrencyString(numericValue);
  },
  [ChecklistItemValueFormat.PERCENTAGE]: value => {
    const numericValue = Number(value);

    if (_.isNaN(numericValue)) {
      return value;
    }

    return `${numericValue}%`;
  },
  [ChecklistItemValueFormat.DATE]: value => formatDate(value, COMMON_DATE_FORMAT),
  [ChecklistItemValueFormat.NUMBER]: value => {
    const numericValue = Number(value);

    if (_.isNaN(numericValue)) {
      return value;
    }

    return new Intl.NumberFormat('en-US').format(numericValue);
  },
  [ChecklistItemValueFormat.TEXT]: value => value,
};

export const formatChecklistValue = ({ value, valueFormat }) => {
  if (_.isNil(value)) {
    return value;
  }

  const formatter = formatChecklistValueByType[valueFormat];

  if (!formatter) {
    return _.isNil(value) ? value : String(value);
  }

  const result = formatter(value);

  return _.isNil(result) ? result : String(result);
};

// What a read of this shape actually carried. Derived from the request rather than assumed, so the
// recorded depth can never claim more than the payload holds.
export const resolveChecklistReadDepth = ({ includeItems, includeCategories }) => {
  if (includeItems) {
    return ChecklistReadDepth.ITEMS;
  }

  return includeCategories ? ChecklistReadDepth.CATEGORIES : ChecklistReadDepth.SELF;
};

// Whether the store holds this node's real rule. Two ways to have it: a checklist-wide read that went
// at least this deep, or a node read that answered for this id alone — the node read is why the depth
// on its own is no longer the whole answer.
export const isChecklistEntityHydrated = ({ hydration, checklistId, entityType, entityId }) => {
  const entry = hydration[checklistId];

  if (!entry) {
    return false;
  }

  return entry.depth >= CHECKLIST_READ_DEPTH_BY_ENTITY_TYPE[entityType] || !!entry.nodes[entityId];
};

// Which node a form is editing, from the ids it was handed. The forms open both routed and as dialogs,
// so the routed selection is not a reliable answer. Type and id are resolved together because they read
// the same precedence — split across two chains they can disagree about which node is being edited.
export const resolveChecklistEntityRef = ({ checklistId, categoryId, itemId }) => {
  if (itemId) {
    return { entityType: ChecklistEntityType.ITEM, entityId: itemId };
  }

  return categoryId
    ? { entityType: ChecklistEntityType.CATEGORY, entityId: categoryId }
    : { entityType: ChecklistEntityType.CHECKLIST, entityId: checklistId };
};

const STATE_ROW_DETERMINATIONS = new Set([
  ChecklistItemDeterminationType.NOT_APPLICABLE,
  ChecklistItemDeterminationType.NEEDS_REVIEW,
]);

const NON_PASSING_RESULT_STATES = new Set([
  ChecklistResultState.FAILED,
  ChecklistResultState.NEEDS_REVIEW,
]);

const KNOWN_RESULT_STATES = new Set(Object.values(ChecklistResultState));

// A Not Applicable / Needs Review row: shown while its visibility holds, its optional text expression
// is the reason, and it can't carry child items.
export const isChecklistStateRowDetermination = determination =>
  STATE_ROW_DETERMINATIONS.has(determination);

// The item's expression returns text (a value, or a state row's reason) rather than true/false.
export const hasChecklistTextExpression = determination =>
  determination === ChecklistItemDeterminationType.VALUE ||
  isChecklistStateRowDetermination(determination);

export const isChecklistLeafOnlyDetermination = determination =>
  determination === ChecklistItemDeterminationType.MANUAL ||
  isChecklistStateRowDetermination(determination);

// Backends that predate result states send no `state`, so it is derived from the definition's
// determination the same way the backend derives it.
const getFallbackResultState = ({ determination, result }) => {
  switch (determination) {
    case ChecklistItemDeterminationType.NOT_APPLICABLE:
      return ChecklistResultState.NOT_APPLICABLE;

    case ChecklistItemDeterminationType.VALUE:
      return ChecklistResultState.INFORMATIONAL;

    case ChecklistItemDeterminationType.NEEDS_REVIEW:
    case ChecklistItemDeterminationType.MANUAL:
      return result ? ChecklistResultState.PASSED : ChecklistResultState.NEEDS_REVIEW;

    default:
      return result ? ChecklistResultState.PASSED : ChecklistResultState.FAILED;
  }
};

const resolveChecklistResultState = ({ state, determination, result }) =>
  KNOWN_RESULT_STATES.has(state) ? state : getFallbackResultState({ determination, result });

// A Value row normally shows its value, but a Value parent over a failed or needs-review child has
// to read that state instead.
export const isChecklistResultStateNonPassing = state => NON_PASSING_RESULT_STATES.has(state);

// The legacy Checklists tab's green and red glyphs already say Passed and Failed; any other status
// needs its word beside the glyph.
export const isChecklistResultGlyphOnly = iconType =>
  iconType === StatusEnum.SUCCESS || iconType === StatusEnum.ERROR;

/**
 * The status glyph, word, text colour and label variant of a checklist item, category or checklist.
 * A known backend `state` reads as sent, because the boolean `result` can differ from it: a Value or
 * overridden parent over an unresolved child passes yet needs review, and a parent's stored boolean
 * lags behind its reviewed child. Only a backend that sends no state falls back to the item
 * definition's `determination` (omit it for a category or a checklist) and `result`. An unknown
 * display type keeps the Failed / Passed glyphs but has no word.
 */
export const getChecklistResultStatus = ({ state, determination, result, statusDisplayType }) => {
  const resolvedState = resolveChecklistResultState({ state, determination, result });

  return (
    CHECKLIST_RESULT_STATE_STATUS_BY_DISPLAY_TYPE[statusDisplayType]?.[resolvedState] ?? {
      ...CHECKLIST_RESULT_STATE_STATUS_BY_DISPLAY_TYPE[ChecklistDisplaySettingType.FINAL_RESULT][
        resolvedState
      ],
      text: null,
    }
  );
};
